<template>
  <div class="wealth-dashboard">
    <header class="page-header">
      <div>
        <h1><i class="fas fa-chart-line"></i> Wealth Management Reviews</h1>
        <p class="subtitle">Quarterly review queue and household overview.</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="loadHouseholds(true)">
          <i class="fas fa-sync"></i> Refresh
        </button>
      </div>
    </header>

    <section class="summary-row">
      <div class="stat-card">
        <div class="stat-label">Households</div>
        <div class="stat-value">{{ totalCount }}</div>
      </div>
      <div class="stat-card highlight">
        <div class="stat-label">Reviews Due</div>
        <div class="stat-value">{{ dueCount }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Completed This Quarter</div>
        <div class="stat-value">{{ completedThisQuarter }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">Avg. IPS Score</div>
        <div class="stat-value">{{ avgScore === null ? '—' : avgScore }}</div>
      </div>
    </section>

    <section class="controls">
      <input
        v-model="searchQuery"
        type="search"
        placeholder="Search households..."
        class="search-input"
      />
      <select v-model="statusFilter" class="filter-select">
        <option value="">All statuses</option>
        <option value="ACTIVE">Active</option>
        <option value="ONBOARDING">Onboarding</option>
        <option value="INACTIVE">Inactive</option>
      </select>
    </section>

    <section v-if="loading && households.length === 0" class="loading-state">
      <div v-for="n in 5" :key="n" class="skeleton-row"></div>
    </section>

    <section v-else-if="households.length === 0" class="empty-state">
      <i class="fas fa-inbox"></i>
      <p>No households yet. Add your first household to get started.</p>
    </section>

    <section v-else class="household-table">
      <table>
        <thead>
          <tr>
            <th>Household</th>
            <th>Advisor</th>
            <th class="num">AUM</th>
            <th>Cadence</th>
            <th>Next Review</th>
            <th>Last Review</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="h in paginatedHouseholds" :key="h.id">
            <td>
              <div class="household-cell">
                <strong>{{ h.name }}</strong>
                <small v-if="h.primary_client_name">{{ h.primary_client_name }}</small>
              </div>
            </td>
            <td>{{ h.primary_advisor_name || '—' }}</td>
            <td class="num">${{ formatCurrency(h.total_aum) }}</td>
            <td>{{ h.review_cadence }}</td>
            <td :class="{ overdue: isOverdue(h.next_review_due) }">
              {{ formatDate(h.next_review_due) }}
            </td>
            <td>{{ formatDate(h.last_review_at) }}</td>
            <td>
              <span class="badge" :class="`badge-${h.status.toLowerCase()}`">
                {{ h.status }}
              </span>
            </td>
            <td class="row-actions">
              <button
                class="btn btn-sm btn-primary"
                :disabled="runningHouseholdId === h.id"
                @click="runReview(h)"
              >
                <i class="fas fa-play"></i>
                {{ runningHouseholdId === h.id ? 'Running…' : 'Run Review' }}
              </button>
              <button
                class="btn btn-sm btn-link"
                @click="openIPS(h)"
                title="Edit IPS"
              >
                <i class="fas fa-file-alt"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="totalCount > pageSize" class="pagination">
        <button
          class="btn btn-sm"
          :disabled="page === 1"
          @click="page -= 1"
        >
          <i class="fas fa-chevron-left"></i> Prev
        </button>
        <span class="page-indicator">
          Page {{ page }} of {{ pageCount }}
        </span>
        <button
          class="btn btn-sm"
          :disabled="page >= pageCount"
          @click="page += 1"
        >
          Next <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </section>

    <section class="recent-runs">
      <h2><i class="fas fa-clipboard-list"></i> Recent Reviews</h2>
      <div v-if="runs.length === 0" class="empty-state small">
        <p>No reviews yet.</p>
      </div>
      <table v-else>
        <thead>
          <tr>
            <th>Household</th>
            <th>Type</th>
            <th>Period</th>
            <th class="num">AUM</th>
            <th class="num">Score</th>
            <th class="num">Findings</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in runs" :key="r.id">
            <td>{{ r.household_name }}</td>
            <td>{{ r.review_type }}</td>
            <td>{{ formatDate(r.period_start) }} – {{ formatDate(r.period_end) }}</td>
            <td class="num">${{ formatCurrency(r.aum_snapshot) }}</td>
            <td class="num">
              <span :class="scoreClass(r.overall_score)">{{ r.overall_score }}</span>
            </td>
            <td class="num">{{ r.finding_count }}</td>
            <td>
              <span class="badge" :class="`badge-run-${r.status.toLowerCase()}`">
                {{ r.status }}
              </span>
            </td>
            <td>
              <router-link
                class="btn btn-sm btn-secondary"
                :to="{ name: 'QuarterlyReviewPacket', params: { id: r.id } }"
              >
                View
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script>
import { debounce } from 'lodash-es'
import api from '@/services/api'
import { UI_CONSTANTS } from '@/constants'

export default {
  name: 'WealthReviewDashboard',

  data() {
    return {
      households: [],
      runs: [],
      loading: false,
      searchQuery: '',
      statusFilter: 'ACTIVE',
      page: 1,
      pageSize: UI_CONSTANTS?.DEFAULT_PAGE_SIZE || 25,
      runningHouseholdId: null,
      debouncedLoad: null,
    }
  },

  computed: {
    totalCount() {
      return this.households.length
    },
    dueCount() {
      const today = new Date().toISOString().slice(0, 10)
      return this.households.filter(
        h => h.next_review_due && h.next_review_due <= today,
      ).length
    },
    completedThisQuarter() {
      const now = new Date()
      const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)
      return this.runs.filter(
        r => r.status === 'COMPLETED' && new Date(r.completed_at) >= quarterStart,
      ).length
    },
    avgScore() {
      const scored = this.runs.filter(r => r.status === 'COMPLETED' && r.overall_score)
      if (!scored.length) return null
      return Math.round(
        scored.reduce((acc, r) => acc + r.overall_score, 0) / scored.length,
      )
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.totalCount / this.pageSize))
    },
    paginatedHouseholds() {
      const start = (this.page - 1) * this.pageSize
      return this.households.slice(start, start + this.pageSize)
    },
  },

  watch: {
    searchQuery() {
      this.debouncedLoad()
    },
    statusFilter() {
      this.debouncedLoad()
    },
  },

  mounted() {
    this.debouncedLoad = debounce(() => this.loadHouseholds(), 300)
    this.loadHouseholds()
    this.loadRuns()
  },

  methods: {
    async loadHouseholds() {
      this.loading = true
      try {
        const params = {}
        if (this.searchQuery) params.search = this.searchQuery
        if (this.statusFilter) params.status = this.statusFilter
        const { data } = await api.get('/wealth/households/', { params })
        this.households = Array.isArray(data) ? data : (data.results || [])
        this.page = 1
      } catch (err) {
        console.error('Failed to load households:', err)
      } finally {
        this.loading = false
      }
    },

    async loadRuns() {
      try {
        const { data } = await api.get('/wealth/review-runs/', {
          params: { page_size: 20 },
        })
        this.runs = (Array.isArray(data) ? data : (data.results || [])).slice(0, 20)
      } catch (err) {
        console.error('Failed to load runs:', err)
      }
    },

    async runReview(h) {
      this.runningHouseholdId = h.id
      try {
        const { data } = await api.post(`/wealth/households/${h.id}/run-review/`, {
          review_type: 'QUARTERLY',
          narrate: true,
        })
        await this.loadRuns()
        if (data && data.id) {
          this.$router.push({ name: 'QuarterlyReviewPacket', params: { id: data.id } })
        }
      } catch (err) {
        alert('Review failed: ' + (err.response?.data?.detail || err.message))
      } finally {
        this.runningHouseholdId = null
      }
    },

    openIPS(h) {
      this.$router.push({ name: 'IPSEditor', params: { householdId: h.id } })
    },

    formatCurrency(val) {
      const n = Number(val || 0)
      return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
    },
    formatDate(d) {
      if (!d) return '—'
      try {
        return new Date(d).toLocaleDateString()
      } catch {
        return d
      }
    },
    isOverdue(d) {
      if (!d) return false
      return new Date(d) < new Date()
    },
    scoreClass(score) {
      if (score == null) return ''
      if (score >= 85) return 'score-good'
      if (score >= 65) return 'score-ok'
      return 'score-bad'
    },
  },
}
</script>

<style scoped>
.wealth-dashboard {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 24px;
}
.page-header h1 {
  margin: 0;
  color: #0f2b4a;
}
.subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
}
.summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}
.stat-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px 16px;
}
.stat-card.highlight {
  border-color: #f97316;
  background: #fff7ed;
}
.stat-label {
  color: #64748b;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #0f2b4a;
}
.controls {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}
.search-input, .filter-select {
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
}
.search-input { flex: 1; max-width: 400px; }
.household-table, .recent-runs {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 24px;
}
table { width: 100%; border-collapse: collapse; }
th, td {
  padding: 10px 8px;
  text-align: left;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}
th {
  background: #f8fafc;
  color: #334155;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
}
.num { text-align: right; font-variant-numeric: tabular-nums; }
.household-cell strong { display: block; color: #0f2b4a; }
.household-cell small { color: #64748b; font-size: 11px; }
.badge {
  display: inline-block; padding: 2px 8px; border-radius: 10px;
  font-size: 11px; font-weight: 600; letter-spacing: 0.03em;
}
.badge-active { background: #dcfce7; color: #166534; }
.badge-onboarding { background: #dbeafe; color: #1e40af; }
.badge-inactive { background: #f1f5f9; color: #475569; }
.badge-offboarded { background: #fee2e2; color: #991b1b; }
.badge-run-completed { background: #dcfce7; color: #166534; }
.badge-run-pending, .badge-run-in_progress { background: #fef3c7; color: #854d0e; }
.badge-run-failed { background: #fee2e2; color: #991b1b; }
.overdue { color: #b91c1c; font-weight: 600; }
.score-good { color: #15803d; font-weight: 700; }
.score-ok { color: #b45309; font-weight: 700; }
.score-bad { color: #b91c1c; font-weight: 700; }
.row-actions { white-space: nowrap; }
.btn {
  border: 1px solid transparent;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-sm { padding: 4px 10px; font-size: 12px; }
.btn-primary { background: #0f2b4a; color: #fff; }
.btn-primary:hover { background: #1e3a5f; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f1f5f9; color: #0f2b4a; border-color: #cbd5e1; }
.btn-link { background: transparent; color: #0f2b4a; }
.loading-state .skeleton-row {
  height: 40px; background: #f1f5f9; border-radius: 4px; margin-bottom: 6px;
  animation: shimmer 1.5s infinite;
}
@keyframes shimmer { 0% { opacity: 0.6; } 50% { opacity: 1; } 100% { opacity: 0.6; } }
.empty-state {
  text-align: center; padding: 40px 20px; color: #64748b; font-size: 14px;
}
.empty-state i { font-size: 32px; color: #cbd5e1; margin-bottom: 10px; display: block; }
.empty-state.small { padding: 20px; }
.pagination {
  display: flex; justify-content: center; align-items: center;
  gap: 12px; padding: 10px 0;
}
.page-indicator { color: #64748b; font-size: 13px; }
</style>
